import {useState} from "react";

import {useImages} from "contexts/images";
import {useSceneScope} from "hooks/useSceneScope";
import {isTextColorLock, sharedHexTextColor} from "libs/textColor";
import ColorPicker from "../ColorPicker";

/**
 * The text colour of the banners (libs/textColor.ts), as an admin chooses it
 * for a default. A colour shows at once on all the banners of the page, or on
 * the selected banners only. "Auto" gives the text back to the logo and the
 * background. The Save button of the panel writes the colours to the default:
 * they are the `textColorLocked` field.
 */

interface Props {
  /** The banners of the page. */
  templateIds: string[];
  /** A colour changed on the page: the next Save must write it. */
  onChange: () => void;
}

export default function TextColorSection({templateIds, onChange}: Props) {
  const {textColorLocked, editTextColorLocked} = useImages() as unknown as {
    textColorLocked: Record<string, unknown>;
    editTextColorLocked: (id: string, value: string | null) => void;
  };
  const {selection} = useSceneScope() as {selection: string[] | null};
  const targets = selection ?? templateIds;
  const shared = sharedHexTextColor(textColorLocked, targets);
  const lockedCount = targets.filter((id) => isTextColorLock(textColorLocked[id])).length;
  // The picker opens on the colour of the banners, or on the last colour chosen.
  const [lastColor, setLastColor] = useState("#ffffff");

  const apply = (value: string | null) => {
    for (const id of targets) editTextColorLocked(id, value);
    onChange();
  };

  const state = shared ?? (lockedCount === 0 ? "auto" : "mixed");
  const bannersLabel = selection
    ? `${targets.length} selected banner${targets.length === 1 ? "" : "s"}`
    : `all ${targets.length} banner${targets.length === 1 ? "" : "s"}`;

  return (
    <div className="default-scene-control text-color-section">
      <span
        className="default-scene-caption"
        title="The colour of the banner texts and headlines. The product name next to the logo keeps the colour of the logo, and banner buttons keep their own colours."
      >
        Text color
      </span>
      <div className="text-color-row">
        <ColorPicker
          value={shared ?? lastColor}
          onChange={(hex) => {
            setLastColor(hex);
            apply(hex);
          }}
          ariaLabel="Text color"
          className="text-color-picker"
        />
        <span className="text-color-state" data-testid="text-color-state">
          {state}
        </span>
        <button
          type="button"
          className="default-scene-level"
          onClick={() => apply(null)}
          disabled={lockedCount === 0 || targets.length === 0}
          title="The text follows the logo and the background again"
        >
          Auto
        </button>
      </div>
      <span className="banner-composition-note">
        Goes to {bannersLabel}. Save below makes it the default.
      </span>
    </div>
  );
}
